

























import { defineComponent, ref, watch } from "@vue/composition-api";
import {
  InputPayeeInformation,
  MasspayField,
  MasspayMethod,
  PayeeInformation,
} from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import { FormFields, FormValue, FormValues } from "@/types";
import BaseForm from "@/components/BaseForm/BaseForm.vue";
import BaseSpinner from "@/components/BaseSpinner";
import { Countries_convert_2to3, Countries_List } from "./masspay_countries";

const defaultValues = {
  masspay: {
    country: undefined,
    withdrawal: undefined,
    currency: undefined,
  },
};

export default defineComponent({
  name: "MassPayPayout",
  components: { BaseSpinner, BaseForm },
  props: {
    hideDescription: { type: Boolean },
    savedValues: { type: Object as () => Partial<InputPayeeInformation>, required: true },
    payeeInformation: { type: Object as () => PayeeInformation },
  },
  setup(props, { emit }) {
    const formValues = ref<FormValues>({
      country: Countries_convert_2to3[props.savedValues.country as keyof typeof Countries_convert_2to3],
      currency: props.savedValues.currency,
    });
    const loading = ref(false);
    const isValid = ref(false);
    const withdrawalMethods = ref<MasspayMethod[]>([]);
    const additionalFields = ref<MasspayField[]>([]);
    const CountriesField = [
      {
        name: "country",
        label: "Choose your country",
        isOptional: false,
        inputType: "select",
        options: Countries_List.map(i => ({ value: i.three_letter_code, text: $t(i.name) })),
        value: Countries_convert_2to3[props.savedValues.country as keyof typeof Countries_convert_2to3],
      },
    ];

    const createFields = (params: any) => {
      const newFormFields: FormFields = [];
      params.forEach((param: any) => {
        newFormFields.push({
          name: param.name || param.token,
          label: $t(param.label),
          rules: [!param.isOptional ? "required" : ""],
          validationMode: "aggressive",
          type: param.inputType === "options" ? "select" : param.inputType,
          placeholder: $t(param.placeholder || param.expectedValue || "Select"),
          options:
            param.inputType === "options"
              ? param.validation.split("|").map((v: string) => ({ value: v, text: v }))
              : param.options,
          pattern: param.validation,
          value: param.value,
          ...(param.type === "CardExpiration" ? { type: "month" } : {}),
        });
        formValues.value = {
          ...formValues.value,
          [param.name || param.token]: param.value,
        };
      });
      return newFormFields;
    };

    const fields = ref<FormFields>(createFields(CountriesField));

    const onFormInput = (v: FormValues) => {
      formValues.value = v;
      emit("input", {
        payoutSystem: "masspay",
        country: formValues.value.country,
        currency: formValues.value.currency,
        masspay: {
          fields: additionalFields.value.map((field: FormValue) => ({
            token: field.token,
            value: formValues.value[field.token] ?? "",
          })),
          attrSetToken: additionalFields.value[0]?.attrSetToken,
          destinationToken: formValues.value?.withdrawal,
        },
      });
    };
    const onValidation = (v: boolean) => {
      isValid.value = v;
      emit("validation", isValid.value && !loading.value);
    };

    const getAvailableWithdrawalMethod = async () => {
      if (!formValues.value.country) return;
      try {
        loading.value = true;
        withdrawalMethods.value = await providers.PayeeInformationProvider.getMasspayMethods({
          data: { country: formValues.value.country },
        });

        const withdrawalOptions = withdrawalMethods.value.map(method => ({
          value: method.destinationToken,
          text: $t(`${method.name}`),
        }));
        const withdrawalsField = [
          {
            name: "withdrawal",
            label: "Withdrawal method",
            inputType: "select",
            isOptional: false,
            options: withdrawalOptions,
          },
        ];

        fields.value = [...fields.value.slice(0, 1), ...createFields(withdrawalsField)];
      } finally {
        loading.value = false;
      }
    };

    const getAvailableCurrencies = () => {
      if (!formValues.value.withdrawal) {
        return;
      }
      const currencyOptions = withdrawalMethods.value
        .find(method => method.destinationToken === formValues.value.withdrawal)
        ?.currencies.map(currency => ({
          value: currency,
          text: $t(`${currency}`),
        }));
      formValues.value = {
        ...formValues.value,
        currency: defaultValues.masspay.currency,
      };

      const currenciesField = [
        {
          name: "currency",
          label: "Currency",
          inputType: "select",
          isOptional: false,
          options: currencyOptions,
        },
      ];
      fields.value = [...fields.value.slice(0, 2), ...createFields(currenciesField)];
    };

    const getRequiredFields = async () => {
      if (!formValues.value.currency) return;
      try {
        loading.value = true;
        additionalFields.value = await providers.PayeeInformationProvider.getMasspayFields({
          data: {
            destinationToken: formValues.value.withdrawal as string,
            currency: formValues.value.currency as string,
          },
        });
        fields.value = [...fields.value.slice(0, 3), ...createFields(additionalFields.value)];
        onFormInput(formValues.value);
      } finally {
        loading.value = false;
      }
    };

    watch(
      () => formValues.value.country,
      () => {
        getAvailableWithdrawalMethod();
      }
    );

    watch(
      () => formValues.value.withdrawal,
      () => {
        getAvailableCurrencies();
      }
    );

    watch(
      () => formValues.value.currency,
      () => {
        getRequiredFields();
      }
    );

    watch(loading, () => {
      emit("validation", isValid.value && !loading.value);
    });

    getAvailableWithdrawalMethod();

    return {
      fields,
      formValues,
      loading,
      onFormInput,
      onValidation,
    };
  },
});
